<template>
  <div class="workflow-builder-wrapper">
    <div class="work-flow-builder" ref="wrapperContainer" @wheel="handleWheel">
      <div
        class="drag-container"
        @mousedown.left="handleMouseDown"
        ref="dragContainer"
        :style="getDragContainerStyles"
        data-draggable-container="true"
        :class="{ 'is-dragging-task': isDraggingTask }"
      >
        <div
          v-for="task in process.tasks"
          class="draggable-task"
          @mousemove="onTaskDrag(task)"
          @mouseup="onTaskDrop(task)"
          @click="onTaskClick(task)"
          :ref="`draggableTask-${task.id}`"
          :key="`${task.name}-${task.id}`"
          :style="getTaskStyles(task)"
          :class="getTaskClasses(task)"
          data-draggable-task="true"
          :data-task-id="task.id"
          :id="task.id"
        >
          <div class="task-wrapper">
            <div
              class="task-dropzone before"
              :class="{ visible: mouseUpTaskID == task.id }"
              v-if="
                getTaskMiscData(task.name, 'dropzones')?.includes('before') &&
                isDropZoneVisible(task, 'before')
              "
            >
              <img
                src="../../assets/img/work-flow-icons/arrow-white.svg"
                class="dropzone-arrow"
                alt="dropzone-arrow"
              />
            </div>
            <div
              class="task-dropzone after"
              :class="{ visible: mouseUpTaskID == task.id }"
              v-if="
                getTaskMiscData(task.name, 'dropzones')?.includes('after') &&
                isDropZoneVisible(task, 'after')
              "
            >
              <img
                src="../../assets/img/work-flow-icons/arrow-white.svg"
                class="dropzone-arrow"
                alt="dropzone-arrow"
              />
            </div>

            <img
              :src="getTaskIcon(task)"
              class="task-image"
              :class="task.name.toLowerCase()"
            />

            <div
              class="task-dropzone condition-true"
              :class="{ visible: mouseUpTaskID == task.id }"
              v-if="
                getTaskMiscData(task.name, 'dropzones')?.includes('true') &&
                isDropZoneVisible(task, 'true')
              "
            >
              <img
                src="../../assets/img/work-flow-icons/arrow-white.svg"
                class="dropzone-arrow"
                alt="dropzone-arrow"
              />
              True
            </div>

            <div
              class="task-dropzone condition-false"
              :class="{ visible: mouseUpTaskID == task.id }"
              v-if="
                getTaskMiscData(task.name, 'dropzones')?.includes('false') &&
                isDropZoneVisible(task, 'false')
              "
            >
              <img
                src="../../assets/img/work-flow-icons/arrow-white.svg"
                class="dropzone-arrow"
                alt="dropzone-arrow"
              />
              False
            </div>

            <div class="task-name-container">
              <div
                :class="`task-condition-identifier ${task.pathType}`"
                v-if="task.hasOwnProperty('pathType')"
              >
                {{ task.pathType }}
              </div>
              <div
                v-if="editCaptionID === task.id"
                class="caption"
                v-click-outside="toggleEditCaption"
              >
                <input
                  v-model="task.caption"
                  ref="taskCaptionInput"
                  placeholder="Title eingeben..."
                />
              </div>
              <div
                class="caption"
                v-else
                @click.stop="toggleEditCaption(task)"
                :ref="`taskCaption-${task.id}`"
              >
                {{ task.caption || task.name }}
              </div>
            </div>

            <div class="task-tools-container">
              <i
                v-if="isUserHasTaskEditAccess(task)"
                class="fas fa-edit"
                @click="setActiveTask(task)"
                v-b-tooltip.hover="{ customClass: 'codelos-tooltip' }"
                title="Bearbeiten"
              ></i>
              <i
                v-if="isUserHasTaskDeleteAccess(task)"
                class="fas fa-trash"
                @click="deleteTask(task)"
                v-b-tooltip.hover="{ customClass: 'codelos-tooltip' }"
                title="Löschen"
              ></i>
            </div>
            <div
              v-if="
                (task.name !== 'Start' || isAdmin) &&
                isUserHasAccess &&
                isFloatingTaskBar &&
                getTaskMiscData(task.name, 'options')?.includes('left')
              "
              class="task-add-wrapper left"
            >
              <WorkFlowFloatingTaskBarToggle
                @click="onToggleFloatingTaskBar($event, task.id, 'left')"
              />
            </div>
            <div
              v-if="
                isUserHasAccess &&
                isFloatingTaskBar &&
                getTaskMiscData(task.name, 'options')?.includes('right')
              "
              class="task-add-wrapper right"
            >
              <WorkFlowFloatingTaskBarToggle
                @click="onToggleFloatingTaskBar($event, task.id, 'right')"
              />
            </div>

            <div
              class="condition-task-add-wrapper"
              :class="{
                'is-justified-end':
                  isFloatingTaskBar &&
                  task.name === 'Condition' &&
                  !getChildTask(task.id, 'true') &&
                  getChildTask(task.id, 'false'),
              }"
            >
              <div
                v-if="
                  isFloatingTaskBar &&
                  task.name === 'Condition' &&
                  getChildTask(task.id, 'true')
                "
                class="task-add-wrapper top"
              >
                <WorkFlowFloatingTaskBarToggle
                  @click="onToggleFloatingTaskBar($event, task.id, 'top')"
                />
              </div>

              <div
                v-if="
                  isFloatingTaskBar &&
                  task.name === 'Condition' &&
                  getChildTask(task.id, 'false')
                "
                class="task-add-wrapper bottom"
              >
                <WorkFlowFloatingTaskBarToggle
                  @click="onToggleFloatingTaskBar($event, task.id, 'bottom')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="zoom-control-container">
        <div
          class="zoom-control"
          @click="onZoomClick(120)"
          v-b-tooltip.hover="{ customClass: 'codelos-tooltip' }"
          title="Rauszoomen"
        >
          <svg
            width="14"
            height="2"
            viewBox="0 0 14 2"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M14 0V2H0V0H14Z" fill="black"></path>
          </svg>
        </div>
        <div class="percentage">{{ getScalePercentage }} %</div>
        <div
          class="zoom-control"
          @click="onZoomClick(-120)"
          v-b-tooltip.hover="{ customClass: 'codelos-tooltip' }"
          title="hineinzoomen"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 -3.49691e-07L8 6L14 6L14 8L8 8L8 14L6 14L6 8L-2.62268e-07 8L-3.49691e-07 6L6 6L6 -2.62268e-07L8 -3.49691e-07Z"
              fill="black"
            />
          </svg>
        </div>
      </div>
      <div
        class="reset-view-container"
        @click="resetView"
        v-b-tooltip.hover="{ customClass: 'codelos-tooltip' }"
        title="Ansicht zurücksetzen"
      >
        <img src="../../assets/img/work-flow-icons/reset-view.svg" />
      </div>
    </div>
    <div
      class="floating-taskbar-wrapper"
      v-if="taskBarInfo.isVisible"
      @mouseleave="hideFloatingTaskbar"
    >
      <WorkFlowFloatingTaskBar
        v-if="taskBarInfo.isVisible"
        :key="`${taskBarInfo.currentTaskId}-${taskBarInfo.taskInsertDirection}-${taskBarInfo.x}`"
        :process="process"
        :tasksArray="tasksArray"
        :tasksMiscData="tasksMiscData"
        :isUserHasAccess="isUserHasAccess"
        :customTasksList="customTasksList"
        :currentTaskId="taskBarInfo.currentTaskId"
        :taskInsertDirection="taskBarInfo.taskInsertDirection"
        :scale="zoom.scale"
        :coordinates="{
          x: taskBarInfo.x,
          y: taskBarInfo.y,
        }"
        @setTaskbarMode="setTaskbarMode"
        @hide="hideFloatingTaskbar"
      />
    </div>
    <component
      :is="activeTask && activeTask.name"
      :process="process"
      :flowRunnerEndpoint="flowRunnerEndpoint"
      :flow-id="Number(process.id)"
      :settings="process.settings"
      :task="activeTask"
      :databaseSchema="databaseSchema"
      :showDatabaseMissing="showDatabaseMissing"
      :isTableIdArray="isTableIdArray"
      :isUserHasAccess="isUserHasAccess"
      :getFileNamesFromProps="getFileNamesFromProps"
      :isFileKeySelectModalVisible="
        fileKeySelectModalInfo.isFileKeySelectModalVisible
      "
      @close="onTaskClose"
      @setShowResultInForm="setShowResultInForm"
      @conditionBuilderResponse="onConditionBuilderResponse"
      @tiggerSpecificEvent="triggerSpecificEvent($event)"
      @showToaster="showToaster"
      @resetErrorMessage="$emit('setRunFlowError', null)"
      @showFileKeySelectModal="showFileKeySelectModal"
    />
    <DeleteConditionModal
      v-if="deleteConditionID"
      :tasks="process.tasks"
      :conditionID="deleteConditionID"
      @close="deleteConditionID = null"
      @onSelect="deleteCondition"
    />
    <fileKeySelect
      @close="hideFileKeySelectModal"
      @save="saveDatabaseAndItem"
      :type="process.dataType"
      :databaseName="process.tableID"
      :databaseSchema="databaseSchema"
      :databases="getCustomDatabases"
      :isLoading="fileKeySelectModalInfo.fileKeySelectModalLoading"
      :showCloseIcon="showFileSelectModalCloseIcon"
      v-if="fileKeySelectModalInfo.isFileKeySelectModalVisible"
      @updateDatabaseId="updateDatabaseId"
      @setFileSelectKeyModalLoader="setFileSelectKeyModalLoader"
    ></fileKeySelect>
  </div>
</template>

<script>
import { jsPlumb } from 'jsplumb';
import Start from '../tasks/start.vue';
import Form from '../tasks/start.vue';
import CustomCode from '../tasks/CustomCode.vue';
import Database from '../tasks/Database.vue';
import Email from '../tasks/email.vue';
import API from '../tasks/API.vue';
import Code from '../tasks/code.vue';
import End from '../tasks/end.vue';
import HTML from '../tasks/htmlEditor.vue';
import AskChatGPT from '../tasks/AskChatGPT.vue';
import PageBuilder from '../tasks/pageBuilderWarp.vue';
import Condition from '../tasks/conditionBuilderWrap.vue';
import Logic from '../tasks/logicBuilderWrap.vue';
import JsonMapper from '../tasks/jsonMapper.vue';
import Redirect from '../tasks/redirect.vue';
import GetItem from '../tasks/GetItem.vue';
import RemoveItem from '../tasks/RemoveItem.vue';
import UpdateItem from '../tasks/UpdateItem.vue';
import AILogic from '../tasks/AILogic.vue';
import SpreadSheet from '../tasks/SpreadSheet.vue';
import DeleteConditionModal from '../modals/DeleteConditionModal.vue';
import WorkFlowFloatingTaskBar from './WorkFlowFloatingTaskBar.vue';
import WorkFlowFloatingTaskBarToggle from './WorkFlowFloatingTaskBarToggle.vue';
import fileKeySelect from '../modals/fileKeySelect.vue';
import { mapGetters } from 'vuex';
import { getCustomDatabaseItem } from '../../services/customdb.service';
import { getApiEndpointVersion } from '@/utils/helpers';

export default {
  name: 'WorkFlowBuilder',
  data() {
    return {
      isDraggingTask: false,
      isDraggingContainer: false,
      activeDragTaskID: null,
      mouseUpTaskID: null,
      highlightedTaskID: null,
      editCaptionID: null,
      activeTask: null,
      deleteConditionID: null,
      panStart: { x: 0, y: 0 },
      translate: { x: 0, y: 0 },
      autoScroll: {
        threshold: 1,
        speed: 2,
        interval: null,
      },
      zoom: {
        scale: 1,
        max: 1.5,
        min: 0.4,
        speed: 0.1,
      },
      taskStart: {
        x: null,
        y: null,
      },

      taskBarInfo: {
        isVisible: false,
        currentTaskId: null,
        taskInsertDirection: null,
        x: 0,
        y: 0,
      },
      isDisableFloatingTaskOutSideClick: false,
      fileKeySelectModalInfo: {
        isFileKeySelectModalVisible: false,
        fileKeySelectModalLoading: false,
      },
    };
  },
  props: {
    process: {
      type: Object,
      required: true,
    },
    tasksArray: {
      type: Array,
      required: true,
    },
    tasksMiscData: {
      type: Object,
      required: true,
    },
    isUserHasAccess: {
      type: Boolean,
      required: true,
      default: false,
    },
    flowRunnerEndpoint: {
      type: String,
      required: true,
    },
    isTableIdArray: {
      type: null | Boolean,
      required: true,
      default: false,
    },
    databases: {
      type: Array,
      required: true,
    },
    getDatabaseSchema: {
      type: Function,
      required: true,
    },
    customTasksList: {
      type: Array,
      required: true,
    },
    runFlowError: {
      type: null | String | Number,
      required: true,
    },
    isAdmin: {
      type: Boolean,
      required: true,
      default: false,
    },
    isFloatingTaskBar: {
      type: Boolean,
      required: true,
    },
  },
  methods: {
    resetView() {
      const rootTask = this.process.tasks.findIndex(
        (el) => el.previousTaskId === null
      );

      if (rootTask > -1) {
        const { x, y } = this.process.tasks[rootTask].shape;
        this.translate = {
          x: (-x + 300) * this.zoom.scale,
          y: (-y + 300) * this.zoom.scale,
        };
        this.validateBoundaries();
      }
    },
    onTaskClose() {
      this.activeTask = null;
    },
    setTaskInput(task) {
      const taskIndex = this.process.tasks.findIndex((el) => el.id === task.id);
      if (taskIndex > -1) {
        this.process.tasks[taskIndex].input = this.getPreviousOutput(
          task.previousTaskId
        );
      }
    },
    onTaskClick(task) {
      this.highlightedTaskID = task.id;
      this.setTaskInput(task);
    },
    setActiveTask(task) {
      this.activeTask = task;
      this.setTaskInput(task);
    },
    getPreviousOutput(previousTaskId) {
      if (previousTaskId) {
        const task = this.process.tasks.filter(
          (item) => item.id == previousTaskId
        );
        if (
          this.process.type == 'action' &&
          this.process.tasks.length > 0 &&
          this.process.formTrigger &&
          task.length > 0
        ) {
          let isAction = task[0].input.find((d) => {
            return (
              d.type == 'timeseries' || d.type == 'custom' || d.type == 'model'
            );
          });
          if (!isAction) task[0].input.push(this.process.formTrigger[0]);
        }
        if (task.length > 0) return task[0].output;
      } else {
        if (this.process.type == 'action' && this.process.formTrigger) {
          return this.process.formTrigger;
        } else return [];
      }
    },
    deleteTask(task) {
      let tasks = structuredClone(this.process.tasks);
      const childTaskIndex = tasks.findIndex(
        (el) => el.previousTaskId === task.id
      );
      if (task.name === 'Condition') {
        if (childTaskIndex === -1) {
          this.process.tasks = [...tasks].filter((el) => el.id !== task.id);
        } else {
          // open DeleteConditionModal
          this.deleteConditionID = task.id;
        }
      } else {
        if (childTaskIndex >= 0) {
          tasks[childTaskIndex].previousTaskId = task.previousTaskId;

          if (task.hasOwnProperty('pathType')) {
            tasks[childTaskIndex].pathType = task.pathType;
          }
        }
        if (this.process.type == 'action' && task.caption == 'Start') {
          const action_input = task.output.filter(
            (t) => t.name == 'custom' || t.name == 'timeseries'
          );
          this.process.formTrigger = action_input;
        }
        this.process.tasks = [...tasks].filter((el) => el.id !== task.id);
      }
    },
    deleteCondition(tasks) {
      this.deleteConditionID = null;
      this.$emit('setProcess', { ...this.process, tasks });
    },
    getDropZonePostion(currentDraggedTask, targetTask) {
      const dropX = currentDraggedTask.shape.x;
      const dropY = currentDraggedTask.shape.y;
      const targetDropZones = this.getTaskMiscData(
        targetTask.name,
        'dropzones'
      );

      if (
        targetDropZones.includes('before') &&
        this.isDropZoneVisible(targetTask, 'before') &&
        dropX >= targetTask.shape.x - 140 && // 110
        dropX < targetTask.shape.x - 50 && // 80
        dropY >= targetTask.shape.y - 35 && // 20
        dropY < targetTask.shape.y + 35 // 20
      ) {
        // task dropped before an element
        return 'before';
      }

      if (
        targetDropZones.includes('after') &&
        this.isDropZoneVisible(targetTask, 'after') &&
        dropX <= targetTask.shape.x + 140 && // 110
        dropX > targetTask.shape.x + 50 && // 80
        dropY >= targetTask.shape.y - 35 && // 20
        dropY < targetTask.shape.y + 35 // 20
      ) {
        // task dropped after an element
        return 'after';
      }

      if (
        targetDropZones.includes('true') &&
        this.isDropZoneVisible(targetTask, 'true') &&
        dropX <= targetTask.shape.x + 110 &&
        dropX > targetTask.shape.x + 80 &&
        dropY >= targetTask.shape.y - 100 && // 80
        dropY < targetTask.shape.y - 15 // 40
      ) {
        // task dropped on true path
        return 'true';
      }

      if (
        targetDropZones.includes('false') &&
        this.isDropZoneVisible(targetTask, 'false') &&
        dropX <= targetTask.shape.x + 110 &&
        dropX > targetTask.shape.x + 80 &&
        dropY >= targetTask.shape.y + 15 && // 40
        dropY < targetTask.shape.y + 100 // 80
      ) {
        // task dropped on false path
        return 'false';
      }
      return null;
    },
    onTaskDrop(currentDraggedTask) {
      const mouseUpTaskID = this.mouseUpTaskID;
      const activeDragTaskID = this.activeDragTaskID;
      if (activeDragTaskID && mouseUpTaskID) {
        let tasks = structuredClone(this.process.tasks);
        const targetTaskID = tasks.findIndex((el) => el.id === mouseUpTaskID);
        const currentTaskID = tasks.findIndex(
          (el) => el.id === currentDraggedTask.id
        );

        const dropType = this.getDropZonePostion(
          tasks[currentTaskID],
          tasks[targetTaskID]
        );

        if (dropType) {
          const getChildTaskIndex = (taskId) => {
            return tasks.findIndex((el) => el.previousTaskId === taskId);
          };
          const draggedChildTaskID = getChildTaskIndex(tasks[currentTaskID].id);

          if (draggedChildTaskID >= 0) {
            tasks[draggedChildTaskID].previousTaskId =
              tasks[currentTaskID].previousTaskId;
          }

          if (tasks[currentTaskID].hasOwnProperty('pathType')) {
            if (draggedChildTaskID >= 0) {
              tasks[draggedChildTaskID].pathType =
                tasks[currentTaskID].pathType;
            }

            delete tasks[currentTaskID].pathType;
          }

          if (dropType === 'before') {
            tasks[currentTaskID].previousTaskId =
              tasks[targetTaskID].previousTaskId;
            tasks[targetTaskID].previousTaskId = tasks[currentTaskID].id;

            if (tasks[targetTaskID].hasOwnProperty('pathType')) {
              tasks[currentTaskID].pathType = tasks[targetTaskID].pathType;
              delete tasks[targetTaskID].pathType;
            }
          }

          if (dropType === 'after') {
            const targetNextSiblingTaskId = tasks.findIndex(
              (el) => el.previousTaskId === tasks[targetTaskID].id
            );
            tasks[currentTaskID].previousTaskId = tasks[targetTaskID].id;
            if (targetNextSiblingTaskId != -1) {
              tasks[targetNextSiblingTaskId].previousTaskId =
                tasks[currentTaskID].id;
            }
          }

          if (dropType === 'false' || dropType === 'true') {
            const targetNextSiblingTaskID = tasks.findIndex(
              (el) =>
                el.pathType?.toString() === dropType &&
                el.previousTaskId === tasks[targetTaskID].id
            );
            if (targetNextSiblingTaskID != -1) {
              tasks[targetNextSiblingTaskID].previousTaskId =
                tasks[currentTaskID].id;
              delete tasks[targetNextSiblingTaskID].pathType;
            }

            tasks[currentTaskID].pathType = dropType;
            tasks[currentTaskID].previousTaskId = tasks[targetTaskID].id;
          }

          this.process.tasks = [...tasks];
        }
      }
    },
    onTaskDrag(task) {
      if (task && !this.isDraggingTask) {
        this.activeDragTaskID = task.id;
      }
    },
    handleMouseDown(e) {
      if (e.target.getAttribute('data-draggable-task')) {
        this.isDraggingTask = true;
        const taskID = e.target.getAttribute('data-task-id');
        if (taskID) {
          const taskRect =
            this.$refs[`draggableTask-${taskID}`][0].getClientRects()[0];
          this.taskStart = {
            x: e.clientX - taskRect.left,
            y: e.clientY - taskRect.top,
          };
        } else {
          this.taskStart = {
            x: null,
            y: null,
          };
        }
      } else if (e.target.getAttribute('data-draggable-container')) {
        this.isDraggingContainer = true;
        this.panStart = {
          x: e.clientX - this.translate.x,
          y: e.clientY - this.translate.y,
        };
      }
    },
    handleMouseMove(e) {
      if (this.isDraggingTask && this.activeDragTaskID !== null) {
        const taskIndex = this.process.tasks.findIndex(
          (el) => el.id === this.activeDragTaskID
        );
        if (taskIndex > -1) {
          if (this.taskBarInfo.isVisible) {
            this.hideFloatingTaskbar();
          }
          this.highlightedTaskID = this.process.tasks[taskIndex].id;
          this.checkDropZone(this.process.tasks[taskIndex]);
          const dragContainer = this.$refs.dragContainer;
          const taskCaption =
            this.$refs[`taskCaption-${this.highlightedTaskID}`];
          const taskCaptionRect = taskCaption[0].getBoundingClientRect();
          const taskOffset = this.process.tasks[taskIndex].hasOwnProperty(
            'pathType'
          )
            ? taskCaptionRect.height + 60 / this.zoom.scale
            : taskCaptionRect.height + 35 / this.zoom.scale;

          const rect = dragContainer.getBoundingClientRect();
          const { height: taskHeight, width: taskWidth } = this.getTaskMiscData(
            this.process.tasks[taskIndex].name,
            'style'
          );

          const xOffset = this.taskStart.x ? this.taskStart.x : taskWidth / 2;
          const yOffset = this.taskStart.y ? this.taskStart.y : taskHeight / 2;

          const x = (e.clientX - rect.x - xOffset) / this.zoom.scale;
          const y = (e.clientY - rect.y - yOffset) / this.zoom.scale;

          if (x < 0) {
            this.process.tasks[taskIndex].shape.x = 0;
          } else if (x + taskWidth > dragContainer.offsetWidth) {
            this.process.tasks[taskIndex].shape.x =
              dragContainer.offsetWidth - taskWidth;
          } else {
            this.process.tasks[taskIndex].shape.x = x;
          }

          if (y - taskOffset < 0) {
            this.process.tasks[taskIndex].shape.y = taskOffset;
          } else if (y + taskHeight > dragContainer.offsetHeight) {
            this.process.tasks[taskIndex].shape.y =
              dragContainer.offsetHeight - taskHeight;
          } else {
            this.process.tasks[taskIndex].shape.y = y;
          }

          this.autoTranslate(taskIndex, taskOffset);
          jsPlumb.repaintEverything();
        }
      }
      if (this.isDraggingContainer) {
        if (this.taskBarInfo.isVisible) {
          this.hideFloatingTaskbar();
        }
        this.translate = {
          x: e.clientX - this.panStart.x,
          y: e.clientY - this.panStart.y,
        };
        this.validateBoundaries();
      }
    },
    autoTranslate(taskIndex, taskOffset) {
      const dragContainer = this.$refs.dragContainer;
      const {
        right: wRight,
        left: wLeft,
        top: wTop,
        bottom: wBottom,
      } = this.$refs.wrapperContainer.getBoundingClientRect();
      const { left: dLeft, top: dTop } = dragContainer.getBoundingClientRect();
      const { height: taskHeight, width: taskWidth } = this.getTaskMiscData(
        this.process.tasks[taskIndex].name,
        'style'
      );
      const zoomScale = this.zoom.scale;
      const { speed, threshold } = this.autoScroll;

      clearInterval(this.autoScroll.interval);
      this.autoScroll.interval = setInterval(() => {
        // X-AXIS ==> RIGHT
        if (
          this.process.tasks[taskIndex].shape.x + taskWidth + threshold >
          (wRight - dLeft) / zoomScale
        ) {
          this.translate.x -= speed;
          this.validateBoundaries();

          if (
            this.process.tasks[taskIndex].shape.x + speed + taskWidth >
            dragContainer.offsetWidth
          ) {
            this.process.tasks[taskIndex].shape.x =
              dragContainer.offsetWidth - taskWidth;
          } else {
            this.process.tasks[taskIndex].shape.x += speed / zoomScale;
          }
        }

        // X-AXIS ==> LEFT
        if (
          this.process.tasks[taskIndex].shape.x <
          (wLeft - dLeft) / zoomScale + threshold
        ) {
          this.translate.x += speed;
          this.validateBoundaries();

          if (this.process.tasks[taskIndex].shape.x - speed < 0) {
            this.process.tasks[taskIndex].shape.x = 0;
          } else {
            this.process.tasks[taskIndex].shape.x -= speed / zoomScale;
          }
        }

        // Y-AXIS ==> BOTTOM
        if (
          this.process.tasks[taskIndex].shape.y + taskHeight + threshold >
          (wBottom - dTop) / zoomScale
        ) {
          this.translate.y -= speed;
          this.validateBoundaries();

          if (
            this.process.tasks[taskIndex].shape.y + speed + taskHeight >
            dragContainer.offsetHeight
          ) {
            this.process.tasks[taskIndex].shape.y =
              dragContainer.offsetHeight - taskHeight;
          } else {
            this.process.tasks[taskIndex].shape.y += speed / zoomScale;
          }
        }

        // Y-AXIS ==> TOP
        if (
          this.process.tasks[taskIndex].shape.y - taskOffset <
          (wTop - dTop) / zoomScale + threshold
        ) {
          this.translate.y += speed;
          this.validateBoundaries();

          if (this.process.tasks[taskIndex].shape.y - speed - taskOffset < 0) {
            this.process.tasks[taskIndex].shape.y = taskOffset;
          } else {
            this.process.tasks[taskIndex].shape.y -= speed / zoomScale;
          }
        }
        jsPlumb.repaintEverything();
      }, 1);
    },
    checkDropZone(task) {
      if (
        !this.isUserHasAccess ||
        !this.getTaskMiscData(task.name, 'isDroppable')
      ) {
        this.mouseUpTaskID = null;
        return;
      }
      const dropX = task.shape.x;
      const dropY = task.shape.y;

      const mouseUpTask = this.process.tasks.find((item) => {
        if (
          dropX >= item.shape.x - 120 &&
          dropX < item.shape.x + 120 &&
          dropY >= item.shape.y - 85 &&
          dropY < item.shape.y + 85 &&
          item.id != task.id
        ) {
          return item;
        }
      });

      if (mouseUpTask) {
        this.mouseUpTaskID = mouseUpTask.id;
      } else {
        this.mouseUpTaskID = null;
      }
    },
    handleMouseUp() {
      clearInterval(this.autoScroll.interval);
      this.isDraggingTask = false;
      this.activeDragTaskID = null;
      this.mouseUpTaskID = null;

      if (this.isDraggingContainer) {
        this.isDraggingContainer = false;
        this.panStart = { x: 0, y: 0 };
      }
      jsPlumb.repaintEverything();
    },
    getBoundingRects() {
      const dragContainer = this.$refs.dragContainer;
      const wrapperContainerRect =
        this.$refs.wrapperContainer.getBoundingClientRect();

      const childRect = {
        left: this.translate.x,
        top: this.translate.y,
        right: dragContainer.clientWidth * this.zoom.scale + this.translate.x,
        bottom: dragContainer.clientHeight * this.zoom.scale + this.translate.y,
      };

      return {
        top: 0 - childRect.top,
        bottom: wrapperContainerRect.height - childRect.bottom,
        right: wrapperContainerRect.width - childRect.right,
        left: 0 - childRect.left,
      };
    },
    validateBoundaries() {
      const { top, bottom, right, left } = this.getBoundingRects();

      if (top < 0) {
        this.translate.y += top;
      }

      if (bottom > 0) {
        this.translate.y += bottom;
      }

      if (right > 0) {
        this.translate.x += right;
      }

      if (left < 0) {
        this.translate.x += left;
      }
    },
    getScaleMultiplier(delta, speed) {
      let sign = Math.sign(delta);
      let deltaAdjustedSpeed = Math.min(0.25, Math.abs((speed * delta) / 128));
      return 1 - sign * deltaAdjustedSpeed;
    },
    getOffsetXY(e) {
      let offsetX, offsetY;
      let ownerRect = this.$refs.wrapperContainer.getBoundingClientRect();
      offsetX = e.clientX - ownerRect.left;
      offsetY = e.clientY - ownerRect.top;

      return { x: offsetX, y: offsetY };
    },
    detectTouchPad(event) {
      if (event.deltaY) {
        if (Math.abs(event.deltaY) <= 110) {
          return true;
        }
      } else if (event.deltaMode === 0) {
        return true;
      }

      return false;
    },
    zoomByRatio(clientX, clientY, ratio) {
      let newScale = this.zoom.scale * ratio;

      if (newScale < this.zoom.min) {
        if (this.zoom.scale === this.zoom.min) return;

        ratio = this.zoom.min / this.zoom.scale;
      }

      if (newScale > this.zoom.max) {
        if (this.zoom.scale === this.zoom.max) return;
        ratio = this.zoom.max / this.zoom.scale;
      }

      this.translate.x = clientX - ratio * (clientX - this.translate.x);
      this.translate.y = clientY - ratio * (clientY - this.translate.y);

      this.zoom.scale *= ratio;
      this.validateBoundaries();
    },
    handleWheel(e) {
      if (e?.target?.closest('.floating-task-bar')) {
        return;
      }
      const isTouchPad = this.detectTouchPad(e);
      if (this.taskBarInfo.isVisible) {
        this.hideFloatingTaskbar();
      }
      // handle zoom on mouse wheel and pinch gestures
      if (e.ctrlKey || !isTouchPad) {
        let deltaY = e.deltaY;
        const speed = isTouchPad ? 0.8 : this.zoom.speed;
        if (e.deltaMode > 0) deltaY *= 100;

        const scaleMultiplier = this.getScaleMultiplier(deltaY, speed);

        if (scaleMultiplier !== 1) {
          const offset = this.getOffsetXY(e);
          this.zoomByRatio(offset.x, offset.y, scaleMultiplier);
        }
      }

      // handle double finger slide to translate
      if (!e.ctrlKey && isTouchPad) {
        if (Math.abs(e.deltaX) > 0 || Math.abs(e.deltaY) > 0) {
          e.preventDefault();
          this.translate.x -= e.deltaX;
          this.translate.y -= e.deltaY;
          this.validateBoundaries();
        }
      }
      e.preventDefault && (e.ctrlKey || !isTouchPad) && e.preventDefault();
    },
    onZoomClick(deltaY) {
      const rect = this.$refs.wrapperContainer.getBoundingClientRect();
      this.handleWheel({
        deltaY,
        deltaMode: -0.5,
        clientX: (rect.left + rect.right) / 2,
        clientY: (rect.top + rect.bottom) / 2,
      });
    },
    createConn(source, target) {
      jsPlumb.connect({
        source,
        target,
        anchors: ['Right', 'Left'],
        endpoints: [
          ['Rectangle', { width: 10, height: 10, cssClass: 'line-end-point' }],
          'Blank',
        ],
        paintStyle: { stroke: '#dddcdf', strokeWidth: 1.5 },
        connector: ['Flowchart', { cornerRadius: 5 }],
        overlays: [['Arrow', { location: 1 }]],
      });
    },
    setTaskConnections(tasks) {
      if (tasks && tasks.length > 0) {
        setTimeout(() => {
          jsPlumb.ready(() => {
            jsPlumb.reset();
            jsPlumb.importDefaults({
              ConnectionsDetachable: false,
            });
            tasks.map((task) => {
              let source = tasks.find(
                (item) => item.id === task.previousTaskId
              );
              if (source) {
                try {
                  this.createConn(
                    this.$refs[`draggableTask-${source.id}`][0],
                    this.$refs[`draggableTask-${task.id}`][0]
                  );
                } catch (error) {
                  this.createConn(
                    this.$refs[`draggableTask-${source.id}`][0],
                    this.$refs[`draggableTask-${task.id}`][0]
                  );
                }
              }
            });
          });
        }, 10);
      }
    },
    isDropZoneVisible(task, identifier) {
      if (identifier === 'before') {
        return (
          (task.name !== 'Start' || this.isAdmin) &&
          this.activeDragTaskID !== task.previousTaskId
        );
      }

      if (identifier === 'after') {
        const activeTask =
          this.activeDragTaskID &&
          this.process.tasks.find((el) => el.id === this.activeDragTaskID);
        return activeTask?.previousTaskId !== task.id;
      }

      if (identifier === 'true' || identifier === 'false') {
        const childTasks = this.process.tasks.filter(
          (el) => el.previousTaskId == task.id
        );

        if (childTasks.length === 0) {
          return true;
        } else {
          const activeTask =
            this.activeDragTaskID &&
            this.process.tasks.find((el) => el.id === this.activeDragTaskID);
          return activeTask?.previousTaskId === task.id
            ? identifier != activeTask?.pathType?.toString()
            : true;
        }
      }

      return true;
    },
    getTaskStyles(task) {
      return {
        left: task.shape.x + 'px',
        top: task.shape.y + 'px',
        height:
          (this.getTaskMiscData(task.name, 'style')
            ? this.getTaskMiscData(task.name, 'style').height
            : 90) + 'px',
        width:
          (this.getTaskMiscData(task.name, 'style')
            ? this.getTaskMiscData(task.name, 'style').width
            : 90) + 'px',
      };
    },
    getTaskClasses(task) {
      return {
        'task-dragged':
          this.activeDragTaskID && this.activeDragTaskID === task.id,
        'task-active':
          this.highlightedTaskID && this.highlightedTaskID === task.id,
        'task-round':
          this.getTaskMiscData(task.name, 'style')?.appearance === 'round',
        'task-square':
          this.getTaskMiscData(task.name, 'style')?.appearance === 'square',
        'task-error': this.runFlowError && this.runFlowError === task.id,
        'task-custom': !this.tasksMiscData[task.name],
      };
    },
    getTaskMiscData(taskName, key) {
      return this.tasksMiscData[taskName]
        ? this.tasksMiscData[taskName][key]
          ? this.tasksMiscData[taskName][key]
          : null
        : null;
    },
    toggleEditCaption(task) {
      if (!this.isUserHasAccess && !['Start', 'Form'].includes(task.name))
        return;

      this.editCaptionID = task?.id || null;

      if (task) {
        setTimeout(() => {
          this.$refs.taskCaptionInput[0].focus();
        }, 100);
      }
    },
    isUserHasTaskEditAccess(task) {
      return ['Code', 'Condition', 'Logic'].includes(task.name)
        ? this.isUserHasAccess
        : true;
    },
    isUserHasTaskDeleteAccess(task) {
      return (
        this.getTaskMiscData(task.name, 'isDeletable') &&
        this.isUserHasAccess &&
        (task.name !== 'Start' || this.isAdmin) &&
        (task.name === 'Start' ? this.getApiVersion > 6 : true)
      );
    },
    async databaseSchema() {
      const process = { ...this.process };
      if (process.type == 'action' && process.hasOwnProperty('tableID')) {
        // Return schema if found else null
        let databaseDetails = null;
        try {
          if (this.isTableIdArray) {
            databaseDetails = this.databases.find(
              (e) => e.name == process.tableID[0]
            );
          }

          if (!this.isTableIdArray) {
            databaseDetails = this.databases.find(
              (e) => e.name == process.tableID
            );
          }
          if (databaseDetails) {
            if (
              !databaseDetails.hasOwnProperty('schema') ||
              databaseDetails.schema === null ||
              databaseDetails.schema === undefined ||
              databaseDetails.schema === ''
            ) {
              return await JSON.parse(
                await this.getDatabaseSchema(databaseDetails.name)
              );
            } else {
              return JSON.parse(databaseDetails.schema);
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log({ error });
          return null;
        }
      }
    },
    setShowResultInForm(value) {
      const startTaskIndex = this.process.tasks.findIndex(
        (el) => el.name === 'Start'
      );
      if (startTaskIndex > -1) {
        if (value) {
          this.process.tasks[
            startTaskIndex
          ].props.formTrigger.structure.tabs[0].rows[0].cols.push({ id: 1 });
        } else {
          if (
            !(
              this.process.tasks[startTaskIndex].props.formTrigger.structure
                .tabs[0].rows[0].cols.length <= 1
            )
          ) {
            this.process.tasks[
              startTaskIndex
            ].props.formTrigger.structure.tabs[0].rows[0].cols.pop();
          }
        }
      }
    },
    onConditionBuilderResponse(emittedData) {
      const conditionTaskIndex = this.process.tasks.findIndex(
        (el) => el.id === emittedData.task.id
      );
      if (conditionTaskIndex > -1)
        this.process.tasks[conditionTaskIndex].conditionResult =
          emittedData.res;
    },
    getFileNamesFromProps(taskId) {
      const availableFileNameTasks = [
        { taskName: 'AskChatGPT', taskVariable: 'chatGptTaskName' },
        { taskName: 'HTML', taskVariable: 'filename' },
        { taskName: 'PageBuilder', taskVariable: 'reportName' },
        { taskName: 'API', taskVariable: 'filename' },
        { taskName: 'Database', taskVariable: 'databaseName' },
        { taskName: 'GetItem', taskVariable: 'elementName' },
        { taskName: 'UpdateItem', taskVariable: 'elementName' },
        { taskName: 'RemoveItem', taskVariable: 'elementName' },
      ];
      const filteredTask = [];
      this.process.tasks.forEach((task) => {
        const isTaskExist = availableFileNameTasks.find(
          (el) => el.taskName === task.name.trim()
        );
        if (isTaskExist && task.id !== taskId) {
          filteredTask.push(task.props[isTaskExist.taskVariable]);
        }
      });
      return filteredTask;
    },
    triggerSpecificEvent(event) {
      if (event.name == 'fetchCustomTaskItem') {
        this.$emit('fetchAndAddSpecificCustomTask', event.id);
      }
    },
    getTaskIcon(task) {
      if (task.customIcon) {
        return task.customIcon;
      }
      if (this.customTasksList.includes(task.name)) {
        return task.icon;
      }
      return require(`../../assets/img/work-flow-icons/task-icons/${
        this.getTaskMiscData(task.name, 'style')?.icon
      }`);
    },
    showToaster(message, title, variant) {
      this.$emit('showToaster', message, title, variant);
    },
    getChildTask(taskID, path) {
      const childTasks = this.process.tasks.filter(
        (el) => el.previousTaskId === taskID
      );
      return !childTasks.some((el) => el?.pathType?.toString() == path);
    },

    setTaskbarMode(el) {
      this.hideFloatingTaskbar();
      this.$emit('setTaskbarMode', el);
    },
    hideFloatingTaskbar() {
      this.taskBarInfo = {
        currentTaskId: null,
        taskInsertDirection: null,
        x: null,
        y: null,
        isVisible: false,
      };
    },

    onToggleFloatingTaskBar(event, currentTaskId, taskInsertDirection) {
      this.isDisableFloatingTaskOutSideClick = true;

      const buttonElement = event.target.classList.contains(
        'floating-taskbar-toggle'
      )
        ? event.target
        : event.target.parentElement;
      const buttonElementRect = buttonElement.getBoundingClientRect();

      this.taskBarInfo.currentTaskId = currentTaskId;
      this.taskBarInfo.taskInsertDirection = taskInsertDirection;
      this.taskBarInfo.x = event.x;
      this.taskBarInfo.x = event.x - (event.x - buttonElementRect.x || 0);
      this.taskBarInfo.y = event.y;

      // this.calculateAndSetFloatingTaskBarPosition();
      this.taskBarInfo.isVisible = true;
      setTimeout(() => {
        this.isDisableFloatingTaskOutSideClick = false;
      });
    },
    hideFileKeySelectModal: function () {
      this.fileKeySelectModalInfo.isFileKeySelectModalVisible = false;
    },
    async saveDatabaseAndItem(key, database) {
      try {
        if (this.activeTask.name === 'Start') {
          if (key) {
            const modelElementIdx =
              this.activeTask.props.formTrigger.elements.findIndex(
                (e) => e.type === 'model'
              );
            if (modelElementIdx > -1) {
              this.activeTask.props.formTrigger.elements[
                modelElementIdx
              ].value = key;
            }
          }
          if (database) {
            this.addSelectedDatabase(database);
          }
        } else if (this.activeTask.name === 'Code') {
          const customTaskIndex = this.activeTask.input.findIndex(
            (task) => task.name === 'custom'
          );
          if (database) {
            this.addSelectedDatabase(database);
          }
          if (customTaskIndex !== -1 && key) {
            this.fileKeySelectModalInfo.fileKeySelectModalLoading = true;
            const findItem = await getCustomDatabaseItem(database, key, true);
            this.activeTask.input[customTaskIndex].value = findItem.data;
            this.fileKeySelectModalInfo.fileKeySelectModalLoading = false;
          }
        }
        this.hideFileKeySelectModal();
      } catch (e) {
        this.fileKeySelectModalInfo.fileKeySelectModalLoading = false;
      }
    },
    addSelectedDatabase(database) {
      if (!this.isTableIdArray) {
        if (this.process.tableID != '') {
          this.process.tableID = [...new Set([this.process.tableID, database])];
        } else this.process.tableID = [database];
      }
      if (this.isTableIdArray) {
        if (
          !(
            this.process.tableID &&
            this.process?.tableID?.length &&
            Array.isArray(this.process.tableID)
          )
        ) {
          this.process.tableID = [];
        }
        const selectedDatabase = Array.isArray(database)
          ? database
          : [database];
        this.process.tableID = [
          ...new Set([...this.process.tableID, ...selectedDatabase]),
        ];
      }
    },
    updateDatabaseId(dbName) {
      if (
        Array.isArray(this.process?.tableID) &&
        this.process?.tableID?.length >= 1
      ) {
        this.process.tableID = this.process.tableID.filter(
          (el) => el !== dbName
        );

        this.fileKeySelectModalInfo.isFileKeySelectModalVisible = false;

        setTimeout(() => {
          this.fileKeySelectModalInfo.isFileKeySelectModalVisible = true;
        }, 100);
      }
    },
    showFileKeySelectModal() {
      this.fileKeySelectModalInfo.isFileKeySelectModalVisible = true;
    },
    setFileSelectKeyModalLoader(loader) {
      this.fileKeySelectModalInfo.fileKeySelectModalLoading = loader;
    },
  },

  mounted() {
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
    this.setTaskConnections(this.process.tasks);

    const zoomScale = localStorage.getItem(
      `workFlowZoom-${this.user.username}`
    );
    const parsedScale = zoomScale && parseFloat(zoomScale);
    if (
      parsedScale &&
      typeof parsedScale === 'number' &&
      parsedScale >= this.zoom.min &&
      parsedScale <= this.zoom.max
    ) {
      this.zoom.scale = parsedScale;
    }
  },
  beforeDestroy() {
    jsPlumb.destroy();
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  },
  watch: {
    'process.tasks'(newval) {
      if (newval && newval.length) {
        this.setTaskConnections(newval);
      }
    },
  },
  computed: {
    ...mapGetters({
      databaseList: 'getDatabaseList',
      user: 'getUser',
    }),
    getScalePercentage() {
      return (this.zoom.scale * 100).toFixed();
      // return ((this.zoom.scale - 1) / (this.zoom.max - 1)) * 100;
    },
    getDragContainerStyles() {
      return {
        transform: `translate(${this.translate.x}px, ${this.translate.y}px) scale(${this.zoom.scale})`,
        position:
          !this.isDraggingContainer && !this.isDraggingTask
            ? 'absolute'
            : this.isDraggingContainer && !this.isDraggingTask
            ? 'absolute'
            : !this.isDraggingContainer && this.isDraggingTask
            ? 'absolute'
            : 'relative',
      };
    },
    showDatabaseMissing() {
      const p = this.process;
      if (p.type == 'action') {
        if (p.hasOwnProperty('tableID')) {
          // Check if database exists
          let database;

          if (this.isTableIdArray) {
            database = this.databases.find((e) =>
              p.tableID.includes(e.name.trim())
            );
          }

          if (!this.isTableIdArray) {
            database = this.databases.find((e) => e.name == p.tableID);
          }
          if (database != undefined) return false;
          else {
            return true;
          }
        } else {
          return true;
        }
      } else return false;
    },
    getCustomDatabases() {
      const dType = this.process.dataType;
      if (dType == 'timeseries' || dType == 'loadprofile') {
        return this.databaseList.filter(
          (e) => e.type == this.process.dataType || e.type == 'loadprofile'
        );
      } else
        return this.databaseList.filter((e) => e.type == this.process.dataType);
    },
    showFileSelectModalCloseIcon() {
      if (this.activeTask.name === 'Code') {
        return false;
      }
      return true;
    },
    getApiVersion() {
      return getApiEndpointVersion(this.flowRunnerEndpoint);
    },
  },
  components: {
    Start,
    Form,
    CustomCode,
    Database,
    Email,
    API,
    Code,
    End,
    HTML,
    AskChatGPT,
    PageBuilder,
    Condition,
    Logic,
    JsonMapper,
    Redirect,
    GetItem,
    UpdateItem,
    RemoveItem,
    AILogic,
    SpreadSheet,
    DeleteConditionModal,
    WorkFlowFloatingTaskBar,
    WorkFlowFloatingTaskBarToggle,
    fileKeySelect,
  },
  directives: {
    clickOutside: {
      bind: function (el, binding, vnode) {
        el.eventOnClick = function (event) {
          // Check that the click was outside the el and its children
          if (!(el == event.target || el.contains(event.target))) {
            // call method provided in attribute value
            vnode.context[binding.expression]();
          }
        };
        document.addEventListener('click', el.eventOnClick);
      },
      unbind: function (el) {
        document.removeEventListener('click', el.eventOnClick);
      },
    },
  },
  emits: [
    'setRunFlowError',
    'setProcess',
    'fetchAndAddSpecificCustomTask',
    'showToaster',
  ],
};
</script>

<style scoped>
.workflow-builder-wrapper {
  height: 100%;
  flex-grow: 1;
}
.work-flow-builder {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  user-select: none;
}

.work-flow-builder .drag-container {
  width: 6000px;
  height: 6000px;
  cursor: grab;
  transform-origin: 0px 0px;
  /* background-color: rgb(255, 255, 255);
    background-image: radial-gradient(rgb(0, 0, 0) 1px, transparent 1px);
    background-size: 18px 18px; */
}

.work-flow-builder .draggable-task {
  position: absolute;
  cursor: pointer;
  z-index: 1;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
  border-radius: 10px;
  -webkit-box-shadow: 1px 1px 12px 1px rgba(0, 0, 0, 0.13);
  -moz-box-shadow: 1px 1px 12px 1px rgba(0, 0, 0, 0.13);
  box-shadow: 1px 1px 12px 1px rgba(0, 0, 0, 0.13);
}

.work-flow-builder .draggable-task.task-square {
  border-radius: 10px;
}

.work-flow-builder .draggable-task.task-round {
  border-radius: 50px;
}

.work-flow-builder .draggable-task.task-custom .task-wrapper .task-image {
  width: 50px;
  height: 50px;
}

.work-flow-builder .draggable-task .task-wrapper .task-image.start,
.work-flow-builder .draggable-task .task-wrapper .task-image.form {
  margin-left: 5px;
}

.work-flow-builder .draggable-task::before {
  content: '';
  background: rgba(222, 225, 231, 0.88);
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  z-index: 2;
}
.work-flow-builder .draggable-task.task-active {
  -webkit-box-shadow: 0px 0px 10px 0px rgba(4, 203, 0, 0.41);
  -moz-box-shadow: 0px 0px 10px 0px rgba(4, 203, 0, 0.41);
  box-shadow: 0px 0px 10px 0px rgba(4, 203, 0, 0.41);
  z-index: 2;
}

.work-flow-builder .draggable-task.task-error {
  -webkit-box-shadow: 1px 1px 20px 1px rgb(255 51 102 / 82%);
  -moz-box-shadow: 1px 1px 20px 1px rgb(255 51 102 / 82%);
  box-shadow: 1px 1px 20px 1px rgb(255 51 102 / 82%);
}

.work-flow-builder .draggable-task .task-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.work-flow-builder .draggable-task .task-image {
  width: 32px;
  height: 32px;
  user-select: none;
}

.work-flow-builder .drag-container .task-dragged {
  z-index: 2;
}

.work-flow-builder .draggable-task .task-dropzone {
  width: 90px;
  height: 90px;
  background: #7c7c7c4d;
  position: absolute;
  border: 1.5px dashed #7c7c7c8a;
  border-radius: 7px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-weight: bold;
}

.work-flow-builder .draggable-task .task-dropzone.visible {
  opacity: 1;
  visibility: visible;
}

.work-flow-builder .draggable-task .task-dropzone.before {
  transform: translateX(-110%);
}

.work-flow-builder .draggable-task .task-dropzone.after {
  transform: translateX(110%);
}

.work-flow-builder .draggable-task .task-dropzone.condition-true {
  transform: translateX(110%) translateY(-60%);
}

.work-flow-builder .draggable-task .task-dropzone.condition-false {
  transform: translateX(110%) translateY(60%);
}

.work-flow-builder .draggable-task .task-dropzone .dropzone-arrow {
  width: 24px;
  transform: rotate(270deg);
}

::v-deep .line-end-point svg rect {
  fill: #dddcdf !important;
}
.work-flow-builder .draggable-task:hover .task-tools-container {
  visibility: visible;
}
.workflow-builder-wrapper
  .drag-container.is-dragging-task
  .task-tools-container {
  visibility: hidden !important;
}
.work-flow-builder .draggable-task .task-tools-container:hover {
  visibility: visible;
}
.work-flow-builder .draggable-task .task-tools-container {
  position: absolute;
  top: 50px;
  padding: 3px 10px;
  display: flex;
  visibility: hidden;
  align-items: center;
  gap: 7px;
  padding-top: 25px;
}

.work-flow-builder .draggable-task .task-tools-container i {
  font-size: 14px;
  color: #a0a9b1;
}

.work-flow-builder .draggable-task .task-tools-container i:hover {
  color: #1f2532;
}

.work-flow-builder
  .draggable-task
  .task-tools-container
  .task-tools-more-dropdown
  ::v-deep
  button {
  padding: 0;
  box-shadow: none;
  margin-bottom: 2px;
}

.work-flow-builder .draggable-task .task-name-container {
  position: absolute;
  top: -165px;
  height: 115px;
  width: 200px;
  text-align: center;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: end;
}

.work-flow-builder .draggable-task .task-name-container .caption {
  height: auto;
  width: 100%;
  font-size: 20px;
  font-weight: 500;
  color: #333343;
  cursor: text;
  max-height: 85px;
}

.work-flow-builder .draggable-task .task-name-container input {
  background-color: transparent;
  border: 0;
  width: 100%;
  text-align: center;
  outline: none;
  cursor: text;
  top: 0;
  position: relative;
  font-size: 20px;
  font-weight: 500;
  color: #333343;
}

.work-flow-builder .draggable-task .task-condition-identifier {
  text-align: center;
  text-transform: uppercase;
  font-size: 16px;
  font-weight: 700;
}

.work-flow-builder .draggable-task .task-condition-identifier.true {
  color: green;
}

.work-flow-builder .draggable-task .task-condition-identifier.false {
  color: red;
}

.work-flow-builder .draggable-task .task-insert-container {
  position: absolute;
  width: 90px;
  height: 90px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.work-flow-builder .draggable-task .task-insert-container .insert-options {
  position: absolute;
}

.work-flow-builder .draggable-task .task-insert-container .insert-options.left {
  left: -20px;
}

.work-flow-builder
  .draggable-task
  .task-insert-container
  .insert-options.right {
  right: -20px;
}

.work-flow-builder .zoom-control-container {
  position: fixed;
  bottom: 19px;
  left: 18px;
  background: #fff;
  border-radius: 5px;
  border: 0.5px solid #b4b5b4;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 38px;
  user-select: none;
  height: 41px;
  width: 136px;
  overflow: hidden;
}

.work-flow-builder .zoom-control-container .zoom-control {
  padding: 10px 16px;
  min-width: 46px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  height: 100%;
}

.work-flow-builder .zoom-control-container .zoom-control:hover {
  background: #efefef;
}

.work-flow-builder .zoom-control-container .percentage {
  min-width: 48px;
  text-align: center;
}
.h-full {
  height: 100%;
}
.w-full {
  width: 100%;
}

.work-flow-builder .reset-view-container {
  position: fixed;
  bottom: 20px;
  left: 180px;
  background: #fff;
  border-radius: 7px;
  border: 0.5px solid #b4b5b4;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 38px;
  width: 38px;
  user-select: none;
  cursor: pointer;
}

.work-flow-builder .reset-view-container:hover {
  background: #efefef;
}

/* Model Override Styles */
::v-deep .modal-backdrop {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
}
::v-deep .modal.task {
  background: #ffffff;
  display: block;
  text-align: left;
  font-size: 12px;
  line-height: 17px;
  color: #9f9f9f;
  border-radius: 19px;
  box-shadow: 1px 1px 50px 20px rgba(0, 0, 0, 0.29);
  width: calc(100% - 158px);
  height: calc(100% - 90px);
  padding: 60px 94px;
  position: relative;
  overflow: hidden;
}

::v-deep .modal.task .modal-close {
  position: absolute;
  top: 32px;
  right: 36px;
  background-image: url(../../assets/img/work-flow-icons/close.svg);
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  width: 14px;
  height: 14px;
  cursor: pointer;
}
::v-deep .modal.task .modal-close:hover {
  opacity: 0.8;
}

::v-deep .modal.task .custom-modal-header {
  justify-content: center;
  font-weight: 500;
  font-size: 30px;
  line-height: 36px;
  color: #333343;
  text-align: center;
}
::v-deep .modal.task .tools {
  position: relative;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  width: 46px;
  height: 199px;
  top: -65%;
  float: right;
}

::v-deep .modal.task .element {
  margin-bottom: 16px;
  height: 46px;
  width: 46px;
}

::v-deep .modal.task .tools .element .button {
  background-color: var(--color-black-lighter);
  fill: #fff;
  stroke: #fff;
  width: 46px;
  height: 46px;
  border-radius: var(--circled);
  border: 0;
  -webkit-box-shadow: var(--elevation-medium);
  box-shadow: var(--elevation-low);
  transform: translateZ(0);
  cursor: pointer;
  outline: 0;
  display: flex;
  -webkit-box-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  align-items: center;
  padding: 0;
  transition: all 0.3s ease-out;
}

::v-deep .modal.task .tools .element .button:hover {
  fill: var(--color-black-lighter);
  stroke: var(--color-black-lighter);
  background: #fff;
  -webkit-box-shadow: var(--elevation-high);
  box-shadow: var(--elevation-high);
}

::v-deep .modal.task .tools .element .button:hover svg {
  border: none;
}

::v-deep .modal.task .paper-clip {
  padding-top: 5px;
}

::v-deep .modal.task .data-fields {
  margin-top: 10px;
}

::v-deep .modal.task .action {
  margin-top: 24px;
}

::v-deep .modal.task .tools .element:hover .info-wrapper {
  display: block;
}

::v-deep .modal.task .info-wrapper {
  position: relative;
  display: none;
  will-change: opacity, visibility;
  -webkit-transition: visibility 0.2s cubic-bezier(0.2, 0.91, 0.85, 0.96) 0.35s,
    opacity 0.2s cubic-bezier(0.2, 0.91, 0.85, 0.96) 0.35s;
  transition: visibility 0.2s cubic-bezier(0.2, 0.91, 0.85, 0.96) 0.35s,
    opacity 0.2s cubic-bezier(0.2, 0.91, 0.85, 0.96) 0.35s;
  margin-top: 0;
  right: 133px;
  top: -39px;
  z-index: 1;
  width: fit-content;
}

::v-deep .modal.task .info-wrapper .info {
  font-family: 'Inv Maison Neue', 'Maison Neue', -apple-system,
    BlinkMacSystemFont, 'Open Sans', open-sans, sans-serif;
  font-size: 12px;
  font-weight: 300;
  line-height: 16px;
  letter-spacing: 0.125px;
  white-space: nowrap;
  color: white;
  background: rgb(23, 25, 29);
  border-radius: 4px;
  padding: 8px 16px;
}

::v-deep .modal.task .toggle-list-wrapper {
  background-color: #ffff;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  position: relative;
  right: 249px;
  -webkit-box-shadow: var(--elevation-high);
  box-shadow: var(--elevation-high);
  max-width: 600px;
  z-index: 1;
  margin-top: -93px;
  top: -56px;
  border-radius: 3px;
  display: inline-block;
  clear: both;
  padding: 17px 0 17px 6px;
  width: 226px;
  margin-top: 0px;
  margin-right: 0px;
  height: 250px;
  left: auto;
  overflow: auto;
}

::v-deep .modal.task .toggle-list-wrapper.data-fields {
  border-radius: 3px;
  display: inline-block;
  padding: 16px 0;
  width: 226px;
  margin-top: 0px;
  height: 250px;
  box-sizing: border-box;
}

::v-deep .modal.task .toggle-list-wrapper .item {
  height: 135px;
  cursor: pointer;
  padding: 8px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
}

::v-deep .modal.task .toggle-list-wrapper.data-fields .item {
  cursor: pointer;
  border: none;
  height: auto;
  padding: 8px;
  text-align: center;
  margin: 0 auto;
  width: 200px;
  box-sizing: border-box;
}

::v-deep .modal.task .toggle-list-wrapper.data-fields .item:hover,
.toggle-list-wrapper.actions .item:hover {
  background: #f0f0f0;
}

::v-deep .modal.task .toggle-list-wrapper.data-fields .item .label,
.toggle-list-wrapper.actions .item .label {
  font-weight: 300;
  color: #464d5d;
}

::v-deep .modal.task .toggle-list-wrapper .item .label {
  overflow: hidden;
  font-size: 12px;
  text-transform: none;
  padding: 0;
  margin: 0;
  margin-top: 0;
  height: auto;
  padding-left: 14px;
  padding-right: 14px;
  color: #596377;
  font-size: 14px;
  box-sizing: border-box;
  line-height: 30px;
}
.workflow-builder-wrapper .drag-container .task-wrapper .task-add-wrapper {
  position: absolute;
  visibility: hidden;
  width: 85px;
  height: 90px;
  display: flex;
  align-items: center;
}

.workflow-builder-wrapper
  .drag-container
  .draggable-task:hover
  .task-add-wrapper {
  visibility: visible;
}

.workflow-builder-wrapper .drag-container .task-wrapper .task-add-wrapper.left {
  right: 51px;
  justify-content: start;
}

.workflow-builder-wrapper
  .drag-container
  .task-wrapper
  .task-add-wrapper.right {
  left: 51px;
  justify-content: end;
}

.workflow-builder-wrapper .drag-container .task-wrapper .task-add-wrapper.top {
  position: unset;
  display: flex;
  justify-content: end;
  align-items: flex-start;
}

.workflow-builder-wrapper
  .drag-container
  .task-wrapper
  .task-add-wrapper.bottom {
  position: unset;
  display: flex;
  justify-content: end;
  align-items: flex-end;
}

.workflow-builder-wrapper
  .drag-container
  .task-wrapper
  .task-add-wrapper.top
  ::v-deep
  .new-task-add-button {
  height: 40px;
  width: 40px;
}

.workflow-builder-wrapper
  .drag-container
  .task-wrapper
  .task-add-wrapper.bottom
  ::v-deep
  .new-task-add-button {
  height: 40px;
  width: 40px;
}

.workflow-builder-wrapper
  .drag-container
  .task-wrapper
  .task-add-wrapper.bottom
  ::v-deep
  .new-task-add-button
  svg {
  width: 14px !important;
}

.workflow-builder-wrapper
  .drag-container
  .task-wrapper
  .task-add-wrapper.top
  ::v-deep
  .new-task-add-button
  svg {
  width: 14px !important;
}

.workflow-builder-wrapper
  .drag-container
  .task-wrapper
  .task-add-wrapper
  ::v-deep
  .btn {
  padding: 0 !important;
}

.work-flow-builder .drag-container.is-dragging-task .task-add-wrapper {
  visibility: hidden !important;
}

.workflow-builder-wrapper
  .drag-container
  .task-wrapper
  .condition-task-add-wrapper {
  position: absolute;
  position: absolute;
  left: 51px;
  height: 110px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: right;
}

.workflow-builder-wrapper
  .drag-container
  .task-wrapper
  .condition-task-add-wrapper.is-justified-end {
  justify-content: flex-end !important;
}
.workflow-builder-wrapper .work-flow-builder .taskbar-popover {
  position: fixed;
  z-index: 1;
}
</style>
